import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import WebService from "../WebService";

interface UserAccess {
  name: string;
  code: string;
  path: string;
  read: boolean;
  write: boolean;
  update: boolean;
  delete: boolean;
}

interface UserInfo {
  user_id: number;
  username: string;
  email: string;
  mobile_number: string;
  role_name: string;
  user_image?: string;
  customer_id?: number | string;
  user_type?: string;
  allowed_clients?: string[];
}

interface AuthState {
  user: UserInfo | null;
  access: UserAccess[];
  loading: boolean;
  error: string | null;
}

const initialState: AuthState = {
  user: null,
  access: [],
  loading: false,
  error: null,
};

// --- Thunk to fetch user info & store user_type ---
export const fetchUserInfo = createAsyncThunk<
  UserInfo,
  void,
  { rejectValue: string }
>("auth/fetchUserInfo", async (_, { rejectWithValue }) => {
  try {
    const res: any = await WebService.getAPI({ action: "/v1/door-management/users/me" });
    const me = res?.data ?? res;

    if (!me || typeof me !== "object" || !me.user_id) {
      return rejectWithValue("User info not found or response invalid.");
    }

    const user_id = me.user_id;
    localStorage.setItem("user_id", user_id.toString());

    // Prefer full list payload when available; fall back to /users/me fields
    let user = me;
    try {
      const detailsRes: any = await WebService.getAPI({
        action: `/v1/door-management/users?user_id=${user_id}`,
      });
      const listed = detailsRes?.data?.users?.[0];
      if (listed?.user_id) {
        user = listed;
      }
    } catch {
      // Keep /users/me data if list endpoint fails
    }

    const userInfo: UserInfo = {
      ...user,
      user_type: user.user_type || me.user_type || undefined,
      allowed_clients: user.allowed_clients || me.allowed_clients || undefined,
    };

    localStorage.setItem("user", JSON.stringify(userInfo));
    return userInfo;
  } catch (err: any) {
    const msg =
      typeof err === "string"
        ? err
        : err.response?.data?.message || err.message || "Unknown error";
    return rejectWithValue(msg);
  }
});

// --- Thunk: fetch “/users/access” ---
export const fetchUserAccess = createAsyncThunk<
  UserAccess[],
  void,
  { rejectValue: string }
>("auth/fetchUserAccess", async (_, { rejectWithValue }) => {
  try {
    const res: any = await WebService.getAPI({ action: "/v1/door-management/users/access" });

    if (!res || !Array.isArray(res.data)) {
      return rejectWithValue("Access rights not found or response invalid.");
    }

    return res.data as UserAccess[];
  } catch (err: any) {
    const msg =
      typeof err === "string"
        ? err
        : err.response?.data?.message || err.message || "Unknown error";
    return rejectWithValue(msg);
  }
});

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    logout(state) {
      state.user = null;
      state.access = [];
      state.error = null;
      state.loading = false;
      localStorage.removeItem("user_id");
      localStorage.removeItem("user");
      localStorage.removeItem("token");
    },
    resetError(state) {
      state.error = null;
    },
    
    hydrateUserFromStorage(state) {
      const userJson = localStorage.getItem("user");
      if (userJson) {
        try {
          state.user = JSON.parse(userJson);
        } catch {
          state.user = null;
        }
      }
    }
  },
  extraReducers: (builder) => {
    // fetchUserInfo
    builder.addCase(fetchUserInfo.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(fetchUserInfo.fulfilled, (state, action: PayloadAction<UserInfo>) => {
      state.user = action.payload;
      state.loading = false;
    });
    builder.addCase(fetchUserInfo.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload || "Failed to fetch user info";
    });

    // fetchUserAccess
    builder.addCase(fetchUserAccess.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(fetchUserAccess.fulfilled, (state, action: PayloadAction<UserAccess[]>) => {
      state.access = action.payload;
      state.loading = false;
    });
    builder.addCase(fetchUserAccess.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload || "Failed to fetch access rights";
    });
  },
});

export const { logout, resetError, hydrateUserFromStorage } = authSlice.actions;
export default authSlice.reducer;
